/**
 * Manual "Check for updates" flow for Settings → General (desktop only). The startup path
 * ({@link ./autoUpdate}#runAutoUpdate) checks once on launch and never relaunches; this is the
 * user-initiated counterpart: check → download+install → offer a relaunch that applies it now.
 *
 * State-machine phases:
 *   idle → checking → up-to-date            (already current)
 *   idle → checking → downloading → ready   (newer version found + installed; relaunch to apply)
 *   any  → error                            (offline / 404 / signature mismatch — swallowed, non-fatal)
 *
 * The check is dependency-injected (matching autoUpdate.ts) so it's testable without a Tauri runtime.
 * On web/Android the caller passes `enabled: false` and simply doesn't render the control.
 */
import { useCallback, useEffect, useState } from 'react';
import { defaultCheck, getAppVersion, relaunchApp, runManualUpdate, type CheckFn } from './autoUpdate.js';

export type UpdatePhase = 'idle' | 'checking' | 'up-to-date' | 'downloading' | 'ready' | 'error';

export interface DesktopUpdaterState {
  /** Running app version (null until resolved / off-desktop). */
  currentVersion: string | null;
  phase: UpdatePhase;
  /** The newer version, once a check finds and installs one. */
  availableVersion?: string;
  /** Message for the `error` phase. */
  error?: string;
}

export interface DesktopUpdater {
  state: DesktopUpdaterState;
  /** Check for, and if found download+install, a newer release. */
  check: () => void;
  /** Relaunch so a downloaded (`ready`) update applies immediately. */
  relaunch: () => void;
}

export function useDesktopUpdater(enabled: boolean, check: CheckFn = defaultCheck): DesktopUpdater {
  const [state, setState] = useState<DesktopUpdaterState>({ currentVersion: null, phase: 'idle' });

  // Resolve the current version once on desktop, so Settings can show it before any check.
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void getAppVersion().then((v) => {
      if (live) setState((s) => ({ ...s, currentVersion: v }));
    });
    return () => {
      live = false;
    };
  }, [enabled]);

  const runCheck = useCallback(() => {
    void runManualUpdate((e) => {
      setState((s) => ({ ...s, phase: e.phase, availableVersion: e.availableVersion, error: e.error }));
    }, check);
  }, [check]);

  const relaunch = useCallback(() => {
    void relaunchApp();
  }, []);

  return { state, check: runCheck, relaunch };
}
